// Small building blocks of the portal look (see PortalUI.css): the pop-up frame and a few icons.
import React, { useState } from 'react';
import './PortalUI.css';

const PATHS = {
  search: <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-4.3-4.3" />,
  back: <path d="M19 12H5M12 19l-7-7 7-7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  building: <><rect x="4" y="3" width="11" height="18" rx="1" /><path d="M15 9h5v12h-5M8 7h3M8 11h3M8 15h3" /></>,
  monitor: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.5 3.2-5.5 7-5.5s7 2 7 5.5" /></>,
  news: <path d="M4 5h13v14H6a2 2 0 01-2-2zM17 9h3v8a2 2 0 01-2 2M8 9h5M8 13h5" />,
  check: <path d="M5 12l5 5L20 7" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  open: <path d="M14 4h6v6M20 4l-9 9M18 14v5H5V6h5" />,
  lock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></>,
  chevron: <path d="M9 6l6 6-6 6" />,
  down: <path d="M6 9l6 6 6-6" />,
  doc: <><path d="M7 3h8l4 4v14H7z" /><path d="M15 3v4h4M10 12h6M10 16h6" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 114 2c-.9.6-1.5 1.1-1.5 2.2M12 17h.01" /></>,
};

export function Svg({ name, size = 14 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{PATHS[name]}</svg>;
}

export function initialsOf(text) {
  return String(text || '?').split(/[ .@]/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

// A pop-up: a header (icon or initials, title, subtitle) with a close button, then whatever follows (a body and a beige foot).
export function Modal({ size = 'm', head, onClose, busy, children }) {
  return (
    <div className="pu-overlay" onClick={() => !busy && onClose()}>
      <div className={`pu-modal pu-modal--${size}`} role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pu-mh">
          {head}
          <button type="button" className="pu-x" onClick={onClose} aria-label="Luk" disabled={busy}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Sorting for the portal tables: click a heading to sort by it, click again to turn it around.
export function useSort(key, dir = 'asc') {
  const [sort, setSort] = useState({ key, dir });
  const toggle = (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }));
  return [sort, toggle];
}

// getters: { columnKey: (row) => value }. Empty values go last, numbers sort as numbers, text the Danish way.
export function sortBy(rows, sort, getters) {
  const get = getters[sort.key];
  if (!get) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a);
    const y = get(b);
    const xe = x === null || x === undefined || x === '';
    const ye = y === null || y === undefined || y === '';
    if (xe && ye) return 0;
    if (xe) return 1;
    if (ye) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
    return String(x).localeCompare(String(y), 'da', { numeric: true }) * dir;
  });
}

export function SortHead({ label, k, sort, onSort }) {
  const on = sort.key === k;
  return (
    <button type="button" className={`pu-sh${on ? ' is-on' : ''}`} onClick={() => onSort(k)}>
      {label}<i>{on ? (sort.dir === 'asc' ? '↑' : '↓') : ''}</i>
    </button>
  );
}
